import React, { useEffect, useLayoutEffect, useContext, useRef } from 'react';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  FluentProvider,
  webLightTheme,
  RadioGroup,
  Radio,
  useFocusableGroup,
} from '@fluentui/react-components';
import { ValidatedBudgetInput } from '@bingads-webui-campaign-react/budget-component-v2';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { BudgetItemLabel, CustomBudgetLabel } from './budget-item';
import { useBudgetSuggestionSelection } from './use-budget-suggestion-selection';
import { BudgetSuggestionTiers, CustomBudget } from '../model/constants';

const getStyles = () => ({
  selectedRadio: {
    border: '1px solid rgba(15, 108, 189, 1)',
    padding: '12px, 16px, 12px, 8px',
    borderRadius: '4px',
  },
  radio: {
    width: '100%',
    '& .fui-Label': {
      width: '100%',
    },
  },
  radioGroup: {
    width: '100%',
  },
});

export const BudgetSuggestionContainer = ({
  data,
  isLoading,
  mode,
  campaignType,
  currency,
  onBudgetValidate,
  onSelectedBudgetChange,
  customPerformanceData,
  defaultBudget,
  bidType,
  minBudget,
  maxBudget,
  showDropdown,
  onSelectCurrency,
  currencies,
  renderExtraOptions,
  initialSelectedBudget,
  isTargetCpaSet,
  showOnlySelectedTier,
  hideDescription,
  enableNaturalTabOrder,
  renderSelectedTierDescription,
}) => {
  const classes = useDefaultStyles(getStyles);
  // containerRef is only used when enableNaturalTabOrder=true (agentic budget card).
  // It is deliberately NOT attached when the prop is absent so existing consumers
  // see zero DOM structure change.
  const containerRef = useRef(null);

  const attributes = useFocusableGroup();

  const i18n = useContext(I18nContext);

  const {
    selectedBudget,
    setSelectedBudget,
    customBudgetResult,
    setCustomBudgetResult,
    hasError,
  } = useBudgetSuggestionSelection({
    data,
    isLoading,
    mode,
    defaultBudget,
    onBudgetValidate,
    onSelectedBudgetChange,
    initialSelectedBudget,
  });

  // All three effects below are gated on enableNaturalTabOrder so they are
  // completely inert for existing consumers of this shared component.
  // These issues were introduced alongside the showOnlySelectedTier prop
  // (agentic campaign draft) and must not affect any other consumer.

  // Fixes tabster-dummy aria-hidden focus + helpbutton extra Tab stops. (#10715748)
  useEffect(() => {
    if (!enableNaturalTabOrder || !containerRef.current) return undefined;
    const fix = () => {
      if (!containerRef.current) return;
      containerRef.current.querySelectorAll('i[data-tabster-dummy][aria-hidden]').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      containerRef.current.querySelectorAll('.helpbutton').forEach((el) => {
        if (el.getAttribute('tabindex') !== '-1') el.setAttribute('tabindex', '-1');
      });
    };
    fix();
    const observer = new MutationObserver(fix);
    observer.observe(containerRef.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [enableNaturalTabOrder]);

  // Removes RadioGroup data-tabster so Tab follows natural DOM order. (#10715774)
  // No deps intentional: Fluent's RadioGroup re-adds data-tabster via its own
  // useLayoutEffect on every render cycle; we must run after it each time.
  // The guard (!enableNaturalTabOrder || !containerRef.current) makes this a
  // no-op for all existing consumers (enableNaturalTabOrder defaults to false).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!enableNaturalTabOrder || !containerRef.current) return;
    containerRef.current.querySelectorAll('.budget-suggestion-group').forEach((el) => {
      if (el.hasAttribute('data-tabster')) el.removeAttribute('data-tabster');
    });
  });

  if (hasError) {
    const errorContent = (
      <ValidatedBudgetInput
        validationProps={{
          onValidate: setCustomBudgetResult,
          data: (customBudgetResult && customBudgetResult.data) || { budget: defaultBudget },
          initialValue: defaultBudget,
          alwaysShowError: (customBudgetResult && customBudgetResult.isValid === false),
        }}
        budgetKey="budget"
        currency={currency || 'USD'}
        label={i18n.getString(_TL_('Set the campaign\'s average daily budget'))}
        className="custom-budget-input"
        minBudget={minBudget}
        maxBudget={maxBudget}
        showDropdown={showDropdown}
        onSelectCurrency={onSelectCurrency}
        currencies={currencies}
      />
    );
    return enableNaturalTabOrder ? <div ref={containerRef}>{errorContent}</div> : errorContent;
  }

  const mainContent = (
    <FluentProvider theme={webLightTheme}>
      {!showOnlySelectedTier && !hideDescription && (
        <div style={{ marginBottom: 5, marginTop: 5 }}>
          {i18n.getString(_TL_('The following budget options are based on performance estimates from businesses with similar campaigns, ad settings, and budgets.'))}
        </div>
      )}
      <RadioGroup defaultValue={selectedBudget} className={classNames('budget-suggestion-group', classes.radioGroup, isLoading ? 'budget-loading' : '')} label="Choose an option" onChange={(e, val) => setSelectedBudget(val.value)}>
        {(
          Object.keys(BudgetSuggestionTiers)
            .filter(tier => !showOnlySelectedTier || tier === selectedBudget)
            .map(tier => (
              <Radio
                key={tier}
                value={tier}
                label={<BudgetItemLabel
                  tier={tier}
                  focusAttributes={attributes}
                  data={data && data[tier]}
                  isSelected={selectedBudget === tier}
                  isLoading={isLoading}
                  mode={mode}
                  currency={currency}
                  campaignType={campaignType}
                  bidType={bidType}
                  isTargetCpaSet={isTargetCpaSet}
                  hasTierDescriptionColumn={!!renderSelectedTierDescription}
                  selectedTierDescription={selectedBudget === tier && renderSelectedTierDescription
                    ? renderSelectedTierDescription(tier)
                    : null}
                />}
                checked={selectedBudget === tier}
                className={selectedBudget === tier ?
                  classNames(classes.selectedRadio, classes.radio, `budget-radio-${tier}`, 'selected')
                  : classNames(`budget-radio-${tier}`, classes.radio)}
              />
            ))
        )
        }
        {(!showOnlySelectedTier || selectedBudget === CustomBudget) && (
          <Radio
            key="CustomBudget"
            value="CustomBudget"
            label={<CustomBudgetLabel
              isSelected={selectedBudget === CustomBudget}
              isLoading={isLoading}
              campaignType={campaignType}
              currency={currency}
              onUpdateCustomBudget={setCustomBudgetResult}
              customBudgetResult={customBudgetResult}
              customPerformanceData={customPerformanceData}
              defaultBudget={defaultBudget}
              bidType={bidType}
              minBudget={minBudget}
              maxBudget={maxBudget}
              showDropdown={showDropdown}
              onSelectCurrency={onSelectCurrency}
              currencies={currencies}
              isTargetCpaSet={isTargetCpaSet}
              hasTierDescriptionColumn={!!renderSelectedTierDescription}
              selectedTierDescription={selectedBudget === CustomBudget && renderSelectedTierDescription
                ? renderSelectedTierDescription(CustomBudget)
                : null}
            />}
            checked={selectedBudget === CustomBudget}
            className={selectedBudget === CustomBudget ?
              classNames(classes.selectedRadio, classes.radio, `budget-radio-${CustomBudget}`, 'selected')
              : classNames(`budget-radio-${CustomBudget}`, classes.radio)}
          />
        )}
        {!showOnlySelectedTier && renderExtraOptions && renderExtraOptions({ selectedBudget, setSelectedBudget, classes })}
      </RadioGroup>
    </FluentProvider>
  );
  return enableNaturalTabOrder ? <div ref={containerRef}>{mainContent}</div> : mainContent;
};

BudgetSuggestionContainer.propTypes = {
  data: PropTypes.shape({}).isRequired,
  isLoading: PropTypes.bool.isRequired,
  mode: PropTypes.string.isRequired,
  campaignType: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  onBudgetValidate: PropTypes.func.isRequired,
  onSelectedBudgetChange: PropTypes.func,
  customPerformanceData: PropTypes.shape({}),
  defaultBudget: PropTypes.number.isRequired,
  bidType: PropTypes.string.isRequired,
  minBudget: PropTypes.number.isRequired,
  maxBudget: PropTypes.number.isRequired,
  showDropdown: PropTypes.bool,
  onSelectCurrency: PropTypes.func,
  currencies: PropTypes.arrayOf(PropTypes.shape({})),
  renderExtraOptions: PropTypes.func,
  initialSelectedBudget: PropTypes.string,
  isTargetCpaSet: PropTypes.bool,
  showOnlySelectedTier: PropTypes.bool,
  hideDescription: PropTypes.bool,
  enableNaturalTabOrder: PropTypes.bool,
  renderSelectedTierDescription: PropTypes.func,
};

BudgetSuggestionContainer.defaultProps = {
  customPerformanceData: null,
  onSelectedBudgetChange: _.noop,
  showDropdown: false,
  onSelectCurrency: _.noop,
  currencies: [],
  renderExtraOptions: null,
  initialSelectedBudget: null,
  isTargetCpaSet: false,
  showOnlySelectedTier: false,
  hideDescription: false,
  enableNaturalTabOrder: false,
  renderSelectedTierDescription: null,
};
